import React, { useCallback, useMemo } from 'react';

import { useAppSelector } from '../../../../hooks/react-redux';
import { ContinentGraphData, ContinentMap } from '../../../shared/analytics/ContinentMap';
import { Spinner } from '../../../shared/spinner/Spinner';
import { getContinentGeometryJSON } from '../../store';
import styles from '../Modules.module.scss';
import { getAllOpinionsForMap, getIsLoading } from '../../../opinions/my-opinions/store/selectors';
import { worldCountriesList } from '../../../constants/worldCountriesList';

const { mandatory1, mandatory2, mandatory3 } = styles;

interface OpinionsMapProps {
    dimensions: {
        height: number;
        width: number;
    }
    analyticsSpinner: boolean;
    reduceMapHeight: boolean;
}

const colourRange = [mandatory1, mandatory2, mandatory3];

export const OpinionsMap: React.FC<OpinionsMapProps> = ({ dimensions, analyticsSpinner, reduceMapHeight }) => {
    const continentGeometry = useAppSelector(getContinentGeometryJSON);
    const opinions = useAppSelector(getAllOpinionsForMap);
    const isFetchingOpinions = useAppSelector(getIsLoading);
    const showSpinner = useMemo(() => continentGeometry.length === 0 || analyticsSpinner || isFetchingOpinions, [continentGeometry.length, analyticsSpinner, isFetchingOpinions]);
    const mapDimensions = useMemo(() => ({ height: (dimensions.height * (reduceMapHeight ? 0.6 : 1)) - 50, width: dimensions.width }), [dimensions, reduceMapHeight]);
    const getTotalOpinionsByCountry = useCallback((name: string): number => opinions.reduce((total, opinion) => {
        if (name === 'United Kingdom of Great Britain and Northern Ireland (the)' && (opinion.jurisdiction === 'England & Wales' || opinion.jurisdiction === 'Scotland') || opinion.jurisdiction === name) {
            return total + 1;
        }
        return total;
    }, 0), [opinions]);

    const getCountryDisabledValue = useCallback((name: string) => {
        let opinionsByJurisdiction = opinions.filter(opinion => (name === 'United Kingdom of Great Britain and Northern Ireland (the)' && (opinion.jurisdiction === 'England & Wales' || opinion.jurisdiction === 'Scotland') || opinion.jurisdiction === name));
        if (opinionsByJurisdiction.length > 0) {
            return !(opinionsByJurisdiction.some(({ bespoke }) => bespoke === 1) || opinionsByJurisdiction.some(({ permittedToView }) => !!permittedToView));
        }
        return false;
    }, [opinions]);

    const graphData: ContinentGraphData[] = useMemo(() => worldCountriesList.map(country => ({ value: getTotalOpinionsByCountry(country.name), disabled: getCountryDisabledValue(country.name), ...country })), [getTotalOpinionsByCountry, getCountryDisabledValue]);

    return (
        <div className={styles.continentMapWrapper} style={{ height: `${dimensions.height}px`, width: `${dimensions.width}px` }}>
            {showSpinner ? <Spinner /> : <ContinentMap key='opinions-map' id='opinions' continentGeometry={continentGeometry} graphData={graphData} height={mapDimensions.height} width={mapDimensions.width} colourRange={colourRange} />}
            <div className={styles.chartHeader}>
                <div className={styles.chartTitle}>Opinion Coverage</div>
            </div>
        </div>
    );
};
